import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { config } from "../configuration/settings";
import { Error } from "../../components/Error";
import Cookies from "js-cookie";

import {
  AUTH_AUDIENCE,
  USER_IMPERSONATION_COOKIE_KEY,
  SUPPORT_EMAIL,
} from "../../constants";

const getParameter = (parameterName: string): string => {
  const parameters = new URLSearchParams(window.location.search);
  return parameters.get(parameterName);
};

export const Auth0ProviderWithHistory: React.FC = ({ children }) => {
  const settings = config.get();

  const getUserfriendlyErrorTitle = (error: string) => {
    if (!error) {
      return null;
    }

    switch (error) {
      case "invitation not found or already used":
        return `Invitation Error`;
      default:
        return `Authentication Error`;
    }
  };

  const getUserfriendlyError = (error: string) => {
    if (!error) {
      return null;
    }

    switch (error) {
      case "user is blocked":
        return `Your account has been deactivated.\nPlease contact C3 Operations: ${SUPPORT_EMAIL}`;
      case "invitation not found or already used":
        return `The requested invitation has already been used or has expired. If you require assistance with your account invitation please contact C3 Operations: ${SUPPORT_EMAIL}.`;
      case "account is not valid":
        return `Account is not valid for authentication.\nPlease contact C3 Operations: ${SUPPORT_EMAIL}.`;
      case "an unexpected error has occurred":
      default:
        return `An unexpected error has occurred processing the request.\nPlease try again and if the issue persists contact C3 Operations: ${SUPPORT_EMAIL}`;
    }
  };

  const [invitationParameter] = useState<string>(getParameter("invitation"));
  const [errorMessage, setErrorMessage] = useState<string>(
    getUserfriendlyError(getParameter("error_description")),
  );
  const [errorTitle] = useState<string>(
    getUserfriendlyErrorTitle(getParameter("error_description")),
  );

  const navigate = useNavigate();

  const impersonationRequest =
    Cookies.get(USER_IMPERSONATION_COOKIE_KEY) ?? undefined;

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  const onErrorClose = () => {
    // This will end up redirecting to the login page
    navigate(window.location.pathname);
    setErrorMessage(null);
  };

  if (errorMessage) {
    return (
      <React.Fragment>
        <Error
          header={<span style={{ whiteSpace: "pre-line" }}>{errorTitle}</span>}
          errorMessage={
            <span style={{ whiteSpace: "pre-line" }}>{errorMessage}</span>
          }
          onClose={onErrorClose}
        />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Auth0Provider
        domain={settings.authDomain}
        clientId={settings.authClientId}
        audience={AUTH_AUDIENCE}
        impersonationRequest={impersonationRequest}
        redirectUri={window.location.origin}
        useRefreshTokens={true}
        invitation={invitationParameter ?? undefined}
        onRedirectCallback={onRedirectCallback}
      >
        {children}
      </Auth0Provider>
    </React.Fragment>
  );
};
